/**
 * Per-browser flag overrides.
 *
 * Overrides live in localStorage and win over whatever the provider says, so
 * QA/support can force a flag state on any environment without a deploy and
 * without touching other users. They are applied by the store rather than by a
 * provider, so the channel keeps working when the provider is swapped.
 *
 * Query syntax (persisted on first load, so it survives SPA navigation):
 *   ?ff=new-table            -> new-table = true
 *   ?ff=!new-table           -> new-table = false
 *   ?ff=layout=compact       -> layout    = 'compact'
 *   ?ff=-new-table           -> remove the override for new-table
 *   ?ff=reset                -> remove every override
 */

const STORAGE_KEY = 'is:feature-flags'

export const readOverrides = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

const writeOverrides = (next) => {
  try {
    if (Object.keys(next).length === 0) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Private mode / storage disabled: overrides are a debug affordance, never break the app.
  }
}

export const setOverride = (key, value = true) =>
  writeOverrides({ ...readOverrides(), [key]: value })

export const clearOverride = (key) => {
  const next = readOverrides()
  delete next[key]
  writeOverrides(next)
}

export const clearOverrides = () => writeOverrides({})

const parseToken = (token) => {
  if (token.startsWith('!')) return { key: token.slice(1), value: false }
  if (token.startsWith('-')) return { key: token.slice(1), remove: true }

  const separator = token.indexOf('=')
  if (separator === -1) return { key: token, value: true }

  const value = token.slice(separator + 1)
  if (value === 'true') return { key: token.slice(0, separator), value: true }
  if (value === 'false') return { key: token.slice(0, separator), value: false }
  return { key: token.slice(0, separator), value }
}

/**
 * Reads `?ff=` from the URL and persists it. Returns true when something changed.
 */
export const consumeQueryOverrides = (search) => {
  const query = search ?? (typeof window === 'undefined' ? '' : window.location.search)
  if (!query) return false

  let raw
  try {
    raw = new URLSearchParams(query).get('ff')
  } catch {
    return false
  }
  if (!raw) return false

  if (raw === 'reset') {
    clearOverrides()
    return true
  }

  const next = readOverrides()
  raw
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
    .forEach((token) => {
      const { key, value, remove } = parseToken(token)
      if (!key) return
      if (remove) delete next[key]
      else next[key] = value
    })

  writeOverrides(next)
  return true
}
